import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  LinearProgress,
  Typography,
  styled,
  Alert,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import React, { useEffect } from 'react';
import { useFetchCeleryTaskStatus, CeleryTaskStatus } from '@/shared/actions/erp/ipv4-groups.actions';

interface Ipv4CreationProgressModalProps {
  open: boolean;
  taskId: string | null;
  onComplete: (success: boolean, result?: CeleryTaskStatus['result']) => void;
  groupName?: string;
}

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 24,
  width: '100%',
  borderRadius: 12,
  backgroundColor: theme.palette.grey[theme.palette.mode === 'light' ? 300 : 700],
  '& .MuiLinearProgress-bar': {
    borderRadius: 12,
    backgroundColor: theme.palette.primary.main,
  },
}));

const StatusIcon: React.FC<{ status: CeleryTaskStatus['status'] }> = ({ status }) => {
  if (status === 'SUCCESS') {
    return <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />;
  }
  if (status === 'FAILURE') {
    return <ErrorIcon sx={{ fontSize: 64, color: 'error.main' }} />;
  }
  return <CircularProgress size={64} />;
};

const getStatusMessage = (taskStatus: CeleryTaskStatus | undefined): string => {
  if (!taskStatus) return 'Iniciando proceso...';

  switch (taskStatus.status) {
    case 'PENDING':
      return 'Preparando creación de IPs...';
    case 'STARTED':
      return 'Iniciando creación de IPs...';
    case 'PROGRESS':
      return taskStatus.info?.status || 'Creando IPs...';
    case 'SUCCESS':
      return taskStatus.result?.message || 'IPs creadas correctamente';
    case 'FAILURE':
      return taskStatus.error || 'Error al crear las IPs';
    case 'RETRY':
      return 'Reintentando...';
    case 'REVOKED':
      return 'Tarea cancelada';
    default:
      return 'Procesando...';
  }
};

const getProgress = (taskStatus: CeleryTaskStatus | undefined): number => {
  if (!taskStatus) return 0;

  switch (taskStatus.status) {
    case 'PENDING':
      return 5;
    case 'STARTED':
      return 10;
    case 'PROGRESS':
      return taskStatus.info?.percent || 0;
    case 'SUCCESS':
      return 100;
    case 'FAILURE':
      return 100;
    default:
      return 0;
  }
};

const Ipv4CreationProgressModal: React.FC<Ipv4CreationProgressModalProps> = ({
  open,
  taskId,
  onComplete,
  groupName,
}) => {
  const { data: taskStatus, isError } = useFetchCeleryTaskStatus(taskId, open && !!taskId);

  const isCompleted = taskStatus?.status === 'SUCCESS';
  const isFailed = taskStatus?.status === 'FAILURE' || isError;
  const isProcessing = !isCompleted && !isFailed;

  const progress = getProgress(taskStatus);
  const statusMessage = getStatusMessage(taskStatus);

  // Efecto para manejar la finalización
  useEffect(() => {
    if (isCompleted) {
      // Esperar 2 segundos antes de cerrar para mostrar el mensaje de éxito
      const timer = setTimeout(() => {
        onComplete(true, taskStatus?.result);
      }, 2000);
      return () => clearTimeout(timer);
    }
    if (isFailed) {
      // No cerrar automáticamente en caso de error
    }
  }, [isCompleted, isFailed, onComplete, taskStatus?.result]);

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isProcessing}
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2,
        },
      }}
    >
      <Box sx={{ textAlign: 'center', pt: 2, pb: 1 }}>
        <Typography variant="h5" component="div" fontWeight="bold">
          {isCompleted ? 'Proceso Completado' : isFailed ? 'Error en el Proceso' : 'Creando Grupo IPv4'}
        </Typography>
        {groupName && (
          <Typography variant="body2" color="text.secondary">
            {groupName}
          </Typography>
        )}
      </Box>

      <DialogContent>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={3}
          py={3}
        >
          {/* Icono de estado */}
          <StatusIcon status={taskStatus?.status || 'PENDING'} />

          {/* Barra de progreso */}
          {isProcessing && (
            <Box width="100%" px={2}>
              <BorderLinearProgress variant="determinate" value={progress} />
              <Box display="flex" justifyContent="space-between" mt={1}>
                <Typography variant="body2" color="text.secondary">
                  {taskStatus?.info?.current || 0} IPs creadas
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {taskStatus?.info?.total || '?'} total
                </Typography>
              </Box>
            </Box>
          )}

          {/* Mensaje de estado */}
          <Typography
            variant="body1"
            textAlign="center"
            color={isFailed ? 'error.main' : 'text.primary'}
          >
            {statusMessage}
          </Typography>

          {/* Porcentaje */}
          {isProcessing && (
            <Typography variant="h4" fontWeight="bold" color="primary">
              {progress}%
            </Typography>
          )}

          {/* Información adicional en éxito */}
          {isCompleted && taskStatus?.result && (
            <Alert severity="success" sx={{ width: '100%' }}>
              <Typography variant="body2">
                Se crearon <strong>{taskStatus.result.total_created}</strong> direcciones IP correctamente.
                {taskStatus.result.routers_associated ? (
                  <> Se asociaron <strong>{taskStatus.result.routers_associated}</strong> routers.</>
                ) : null}
              </Typography>
            </Alert>
          )}

          {/* Información de error */}
          {isFailed && (
            <>
              <Alert severity="error" sx={{ width: '100%' }}>
                <Typography variant="body2">
                  {taskStatus?.error || 'Ocurrió un error durante la creación de las IPs. Por favor intente nuevamente.'}
                </Typography>
              </Alert>
              <Button
                variant="contained"
                color="primary"
                onClick={() => onComplete(false)}
              >
                Cerrar
              </Button>
            </>
          )}

          {/* Advertencia */}
          {isProcessing && (
            <Alert severity="warning" sx={{ width: '100%' }}>
              <Typography variant="body2">
                Por favor no cierre esta ventana hasta que el proceso termine.
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default Ipv4CreationProgressModal;
